import { api } from './api';
import type { StudentCreatePayload } from './classroomsApi';

export type SecretariatTeacherRow = {
  id: number;
  full_name: string;
  email: string;
  phone?: string | null;
  created_at?: string | null;
};

export type SecretariatCreateTeacherPayload = {
  full_name: string;
  email: string;
  phone?: string | null;
};

export type SecretariatSyncedClassRow = {
  name: string;
  school_class_id: number;
  classroom_id: number;
  archived_at?: string | null;
  archived_school_year?: string | null;
  admin_professor_id?: number | null;
  admin_professor_name?: string | null;
  admin_professor_email?: string | null;
};

export type SecretariatArchivedCatalogRow = {
  config_id: number;
  classroom_id: number;
  classroom_name: string;
  school_class_id?: number | null;
  school_year: string;
  structure: string;
  education_level: string;
  locked: boolean;
  archived_at?: string | null;
  archived_class_name?: string | null;
  class_archived_at?: string | null;
  class_archived_school_year?: string | null;
};

export type SecretariatStudentDossierRow = {
  id: number;
  full_name: string;
  email?: string | null;
  phone?: string | null;
  school_class_id?: number | null;
  classroom_id?: number | null;
  classroom_name?: string | null;
  is_graduated?: boolean | null;
  graduated_at?: string | null;
  graduated_school_year?: string | null;
};

export type SecretariatStudentDocumentRow = {
  id: number;
  doc_type?: string | null;
  title?: string | null;
  original_name: string;
  mime_type?: string | null;
  size?: number | null;
  created_at?: string | null;
};

export type SecretariatRegistryRow = {
  id: number;
  registry_year: string;
  registry_number: number;
  category: string;
  direction: string;
  request_type: string;
  subject?: string | null;
  description?: string | null;
  doc_number?: string | null;
  issuer?: string | null;
  recipient?: string | null;
  requester_name?: string | null;
  requester_email?: string | null;
  requester_phone?: string | null;
  student_id?: number | null;
  student_name?: string | null;
  status: string;
  created_at?: string | null;
  updated_at?: string | null;
};

export type SecretariatRegistryAttachmentRow = {
  id: number;
  title?: string | null;
  original_name: string;
  mime_type?: string | null;
  size?: number | null;
  created_at?: string | null;
};

export type SecretariatGoogleSheetLinkRow = {
  id: number;
  title: string;
  url: string;
  created_at?: string | null;
  created_by_id?: number | null;
};

export type SchoolListRow = {
  id: number;
  name: string;
  city?: string | null;
  county?: string | null;
};

export type SecretariatTransferRow = {
  id: number;
  from_school_id: number;
  from_school_name?: string | null;
  to_school_id: number;
  to_school_name?: string | null;
  student_id?: number | null;
  student_full_name: string;
  student_email?: string | null;
  status: string;
  message?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  processed_at?: string | null;
  requested_by_user_id?: number | null;
  requested_by_name?: string | null;
  processed_by_user_id?: number | null;
  processed_by_name?: string | null;
  assigned_school_class_id?: number | null;
  assigned_classroom_name?: string | null;
  files_count?: number | null;
};

export type SecretariatTransferFileRow = {
  id: number;
  kind: string;
  original_name: string;
  mime_type?: string | null;
  size?: number | null;
  created_at?: string | null;
};

export async function listSecretariatClasses(opts?: { archived?: boolean }): Promise<SecretariatSyncedClassRow[]> {
  const resp = await api.get('/api/secretariat/classes', { params: { archived: opts?.archived ? 1 : 0 } });
  return Array.isArray(resp.data) ? (resp.data as any) : [];
}

export async function listSecretariatArchivedCatalogs(opts?: { q?: string; school_year?: string | null }): Promise<SecretariatArchivedCatalogRow[]> {
  const resp = await api.get('/api/secretariat/archive/catalogs', {
    params: { q: opts?.q || undefined, school_year: opts?.school_year || undefined },
  });
  return Array.isArray(resp.data) ? (resp.data as any) : [];
}

export async function listSecretariatDossierStudents(opts?: {
  q?: string;
  classroom_id?: number | null;
  include_graduated?: boolean;
  limit?: number;
}): Promise<SecretariatStudentDossierRow[]> {
  const resp = await api.get('/api/secretariat/students', {
    params: {
      q: opts?.q || undefined,
      classroom_id: opts?.classroom_id ?? undefined,
      include_graduated: opts?.include_graduated ? 1 : 0,
      limit: opts?.limit ?? 50,
    },
  });
  return Array.isArray(resp.data) ? (resp.data as any) : [];
}

export async function listSecretariatStudentDocuments(studentId: number): Promise<SecretariatStudentDocumentRow[]> {
  const resp = await api.get(`/api/secretariat/students/${studentId}/documents`);
  return Array.isArray(resp.data) ? (resp.data as any) : [];
}

export async function uploadSecretariatStudentDocument(
  studentId: number,
  payload: { file: File; doc_type?: string | null; title?: string | null }
): Promise<{ id: number }> {
  const form = new FormData();
  form.append('file', payload.file);
  if (payload.doc_type) form.append('doc_type', payload.doc_type);
  if (payload.title) form.append('title', payload.title);
  const resp = await api.post(`/api/secretariat/students/${studentId}/documents`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return resp.data as any;
}

export async function deleteSecretariatStudentDocument(docId: number): Promise<{ detail: string }> {
  const resp = await api.delete(`/api/secretariat/students/documents/${docId}`);
  return resp.data as any;
}

export function getSecretariatStudentDocumentDownloadUrl(docId: number): string {
  return `/api/secretariat/students/documents/${docId}/download`;
}

export async function listSecretariatReportYears(): Promise<string[]> {
  const resp = await api.get('/api/secretariat/reports/years');
  const years = (resp.data as any)?.years;
  return Array.isArray(years) ? years.map((y) => String(y)) : [];
}

export function getSecretariatReportStudentsCsvUrl(opts?: { q?: string; classroom_id?: number | null }): string {
  const params = new URLSearchParams();
  if (opts?.q) params.set('q', String(opts.q));
  if (opts?.classroom_id != null) params.set('classroom_id', String(opts.classroom_id));
  const qs = params.toString();
  return `/api/secretariat/reports/students.csv${qs ? `?${qs}` : ''}`;
}

export function getSecretariatReportAbsencesCsvUrl(opts?: { school_year?: string | null; classroom_id?: number | null }): string {
  const params = new URLSearchParams();
  if (opts?.school_year) params.set('school_year', String(opts.school_year));
  if (opts?.classroom_id != null) params.set('classroom_id', String(opts.classroom_id));
  const qs = params.toString();
  return `/api/secretariat/reports/absences-summary.csv${qs ? `?${qs}` : ''}`;
}

export function getSecretariatReportGradesCsvUrl(opts?: { school_year?: string | null; classroom_id?: number | null }): string {
  const params = new URLSearchParams();
  if (opts?.school_year) params.set('school_year', String(opts.school_year));
  if (opts?.classroom_id != null) params.set('classroom_id', String(opts.classroom_id));
  const qs = params.toString();
  return `/api/secretariat/reports/grades.csv${qs ? `?${qs}` : ''}`;
}

export async function listSecretariatRegistry(opts?: {
  q?: string;
  category?: string | null;
  direction?: string | null;
  status?: string | null;
  limit?: number;
}): Promise<SecretariatRegistryRow[]> {
  const resp = await api.get('/api/secretariat/registry', {
    params: {
      q: opts?.q || undefined,
      category: opts?.category || undefined,
      direction: opts?.direction || undefined,
      status: opts?.status || undefined,
      limit: opts?.limit ?? 200,
    },
  });
  return Array.isArray(resp.data) ? (resp.data as any) : [];
}

export async function createSecretariatRegistry(payload: {
  category?: string | null;
  direction?: string | null;
  request_type: string;
  subject?: string | null;
  description?: string | null;
  doc_number?: string | null;
  issuer?: string | null;
  recipient?: string | null;
  requester_name?: string | null;
  requester_email?: string | null;
  requester_phone?: string | null;
  student_id?: number | null;
}): Promise<SecretariatRegistryRow> {
  const resp = await api.post('/api/secretariat/registry', payload);
  return resp.data as any;
}

export async function updateSecretariatRegistryStatus(entryId: number, status: string): Promise<SecretariatRegistryRow> {
  const resp = await api.put(`/api/secretariat/registry/${entryId}/status`, { status });
  return resp.data as any;
}

export async function listSecretariatRegistryAttachments(entryId: number): Promise<SecretariatRegistryAttachmentRow[]> {
  const resp = await api.get(`/api/secretariat/registry/${entryId}/attachments`);
  return Array.isArray(resp.data) ? (resp.data as any) : [];
}

export async function uploadSecretariatRegistryAttachment(
  entryId: number,
  payload: { file: File; title?: string | null }
): Promise<{ id: number }> {
  const form = new FormData();
  form.append('file', payload.file);
  if (payload.title) form.append('title', payload.title);
  const resp = await api.post(`/api/secretariat/registry/${entryId}/attachments`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return resp.data as any;
}

export async function deleteSecretariatRegistryAttachment(attId: number): Promise<{ detail: string }> {
  const resp = await api.delete(`/api/secretariat/registry/attachments/${attId}`);
  return resp.data as any;
}

export function getSecretariatRegistryAttachmentDownloadUrl(attId: number): string {
  return `/api/secretariat/registry/attachments/${attId}/download`;
}

export async function archiveSecretariatClass(
  classroomId: number,
  payload?: { archived_school_year?: string | null }
): Promise<SecretariatSyncedClassRow> {
  const resp = await api.post(`/api/secretariat/classes/${classroomId}/archive`, {
    archived_school_year: payload?.archived_school_year ?? null,
  });
  return resp.data as any;
}

export async function listSecretariatTeachers(): Promise<SecretariatTeacherRow[]> {
  const resp = await api.get('/api/secretariat/teachers');
  return Array.isArray(resp.data) ? (resp.data as any) : [];
}

export async function createSecretariatTeacher(payload: SecretariatCreateTeacherPayload): Promise<{ id: number; email: string; email_sent: boolean }> {
  const resp = await api.post('/api/secretariat/teachers', payload);
  return resp.data as any;
}

export async function resendSecretariatTeacherInvite(teacherId: number): Promise<{ email_sent: boolean }> {
  const resp = await api.post(`/api/secretariat/teachers/${teacherId}/resend-invite`);
  return resp.data as any;
}

export async function addStudentToClassroomAsSecretariat(
  classroomId: number,
  payload: StudentCreatePayload
): Promise<{ student_id: number; parents_notified?: boolean }> {
  const resp = await api.post(`/api/secretariat/classrooms/${classroomId}/students`, payload);
  return resp.data as any;
}

export async function renameSecretariatClass(
  classroomId: number,
  name: string,
  opts?: { archive_existing_data?: boolean }
): Promise<{ name: string; school_class_id: number; classroom_id: number }> {
  const resp = await api.put(`/api/secretariat/classes/${classroomId}`, {
    name,
    archive_existing_data: Boolean(opts?.archive_existing_data),
  });
  return resp.data as any;
}

export async function listSecretariatGoogleSheets(): Promise<SecretariatGoogleSheetLinkRow[]> {
  const resp = await api.get('/api/secretariat/google-sheets');
  return Array.isArray(resp.data) ? (resp.data as any) : [];
}

export async function createSecretariatGoogleSheet(payload: { title: string; url: string }): Promise<SecretariatGoogleSheetLinkRow> {
  const resp = await api.post('/api/secretariat/google-sheets', payload);
  return resp.data as any;
}

export async function updateSecretariatGoogleSheet(
  sheetId: number,
  payload: { title?: string | null; url?: string | null }
): Promise<SecretariatGoogleSheetLinkRow> {
  const resp = await api.put(`/api/secretariat/google-sheets/${sheetId}`, payload);
  return resp.data as any;
}

export async function deleteSecretariatGoogleSheet(sheetId: number): Promise<{ deleted: boolean }> {
  const resp = await api.delete(`/api/secretariat/google-sheets/${sheetId}`);
  return resp.data as any;
}

export async function listAllSchools(): Promise<SchoolListRow[]> {
  const resp = await api.get('/api/scoli/schools');
  return Array.isArray(resp.data) ? (resp.data as any) : [];
}

export async function listSecretariatOutgoingTransfers(opts?: { status?: string | null; limit?: number; offset?: number }): Promise<SecretariatTransferRow[]> {
  const resp = await api.get('/api/secretariat/transfers/outgoing', {
    params: { status: opts?.status || undefined, limit: opts?.limit ?? 50, offset: opts?.offset ?? 0 },
  });
  return Array.isArray(resp.data) ? (resp.data as any) : [];
}

export async function listSecretariatIncomingTransfers(opts?: { status?: string | null; limit?: number; offset?: number }): Promise<SecretariatTransferRow[]> {
  const resp = await api.get('/api/secretariat/transfers/incoming', {
    params: { status: opts?.status || undefined, limit: opts?.limit ?? 50, offset: opts?.offset ?? 0 },
  });
  return Array.isArray(resp.data) ? (resp.data as any) : [];
}

export async function createSecretariatTransfer(payload: {
  student_id: number;
  to_school_id: number;
  message?: string | null;
}): Promise<SecretariatTransferRow> {
  const resp = await api.post('/api/secretariat/transfers', payload);
  return resp.data as any;
}

export async function acceptSecretariatTransfer(
  transferId: number,
  payload?: { assigned_school_class_id?: number | null; message?: string | null }
): Promise<SecretariatTransferRow> {
  const resp = await api.post(`/api/secretariat/transfers/${transferId}/accept`, {
    assigned_school_class_id: payload?.assigned_school_class_id ?? null,
    message: payload?.message ?? null,
  });
  return resp.data as any;
}

export async function rejectSecretariatTransfer(transferId: number, payload?: { message?: string | null }): Promise<SecretariatTransferRow> {
  const resp = await api.post(`/api/secretariat/transfers/${transferId}/reject`, { message: payload?.message ?? null });
  return resp.data as any;
}

export function getSecretariatTransferExportUrl(transferId: number): string {
  return `/api/secretariat/transfers/${transferId}/export.json`;
}

export async function listSecretariatTransferFiles(transferId: number): Promise<SecretariatTransferFileRow[]> {
  const resp = await api.get(`/api/secretariat/transfers/${transferId}/files`);
  return Array.isArray(resp.data) ? (resp.data as any) : [];
}

export function getSecretariatTransferFileDownloadUrl(fileId: number): string {
  return `/api/secretariat/transfers/files/${fileId}/download`;
}
